/**
 * DiversityScorePill — surfaces the cohort-level Wave Diversity Score
 * (Invention 7) in the wave builder. Reads `diversity` shape:
 *   { diversity_score, audience_reach_coverage_pct,
 *     mean_pairwise_overlap, max_pairwise_overlap, high_overlap_pairs, n }
 *
 * Visual: tiny pill (low diversity = pink, high diversity = teal). Click
 * to expand and inspect the highest-overlap pairs. Designed to nest
 * inline next to existing wave-builder cohort headers without taking up
 * its own row.
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Layers, ShieldAlert } from 'lucide-react';

const T = '#00D4FF';
const P = '#FF4D94';

function _color(score) {
  if (score >= 75) return T;       // teal — diverse
  if (score >= 55) return '#A57FFF'; // violet — middling
  return P;                          // pink — overlap heavy
}

export default function DiversityScorePill({ diversity, label, compact = false }) {
  const [open, setOpen] = useState(false);
  if (!diversity || diversity.n < 2) return null;

  const score = diversity.audience_reach_coverage_pct ?? diversity.diversity_score ?? 0;
  const color = _color(score);
  const pairs = diversity.high_overlap_pairs || [];
  const hasOverlap = pairs.length > 0;

  return (
    <div data-testid="diversity-score-pill" className="inline-flex flex-col items-start">
      <button
        type="button"
        onClick={() => hasOverlap && setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 ${compact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-[11px]'} rounded font-black tracking-wider uppercase border transition`}
        style={{
          background: `${color}14`,
          borderColor: `${color}55`,
          color,
          cursor: hasOverlap ? 'pointer' : 'default',
        }}
        title={hasOverlap
          ? `${pairs.length} high-overlap pair${pairs.length === 1 ? '' : 's'} — click to inspect`
          : 'Audience reach coverage across the selected cohort. Higher = more unique micro-communities reached.'}
      >
        <Layers className="h-2.5 w-2.5" />
        {label || 'Audience reach'}
        <span className="font-black">{Number(score).toFixed(0)}%</span>
        {hasOverlap && (open ? <ChevronUp className="h-2.5 w-2.5" /> : <ChevronDown className="h-2.5 w-2.5" />)}
      </button>

      {open && hasOverlap && (
        <div
          data-testid="diversity-score-pairs"
          className="mt-1.5 ml-1 px-2 py-1.5 rounded border border-[#1F1F1F] bg-[#0A0A0A] text-[10px] space-y-0.5 max-w-[280px]"
        >
          <p className="text-gray-500 uppercase tracking-wider text-[8px] font-semibold mb-1">
            High-overlap pairs (≥30%)
          </p>
          {pairs.slice(0, 5).map((pair, i) => (
            <div key={`${pair.a}-${pair.b}-${i}`} className="flex items-center justify-between gap-2">
              <span className="text-gray-300 truncate">
                <ShieldAlert className="h-2.5 w-2.5 inline-block mr-0.5" style={{ color: P }} />
                {pair.a} ↔ {pair.b}
              </span>
              <span className="font-mono font-bold" style={{ color: P }}>
                {Math.round((pair.overlap || 0) * 100)}%
              </span>
            </div>
          ))}
          {pairs.length > 5 && (
            <p className="text-gray-600 text-[9px] pt-0.5">+{pairs.length - 5} more</p>
          )}
        </div>
      )}
    </div>
  );
}
